import {
  apiRequest,
  API_BASE_URL,
  getAuthToken,
  appendUserIdQueryParam,
} from "./apiClient";
import { userTimezoneHeaders } from "../utils/userTimezoneHeader";



/**
 * User API endpoints
 */

export const userAPI = {
  /**
   * Get all users with pagination and search
   */
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await apiRequest(`/users?${queryString}`);
    return response.data; // Extract data from response
  },

  /**
   * Get user by ID
   */
  getById: async (id) => {
    const response = await apiRequest(`/users/${id}`);
    return response.data;
  },

  /**
   * Create new user
   */
  create: async (userData) => {
    const response = await apiRequest("/users", {
      method: "POST",
      body: JSON.stringify(userData),
    });
    return response.data;
  },

  /**
   * Update user
   */
  update: async (id, userData) => {
    const response = await apiRequest(`/users/${id}`, {
      method: "PUT",
      body: JSON.stringify(userData),
    });
    return response.data;
  },

  /**
   * Delete user
   */
  delete: async (id) => {
    return apiRequest(`/users/${id}`, {
      method: "DELETE",
    });
  },

  /**
   * Get user dependencies (sessions, etc.)
   */
  getDependencies: async (id) => {
    const response = await apiRequest(`/users/${id}/dependencies`);
    return response.data;
  },

  /**
   * Download users as CSV
   */
  downloadUsers: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await fetch(
      `${API_BASE_URL}/users/download?${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "text/csv",
          ...(getAuthToken() && { Authorization: `Bearer ${getAuthToken()}` }),
          ...userTimezoneHeaders(),
        },
      },
    );
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `users_export_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    return { filename: a.download };
  },
};


/**
 * User Settings API endpoints
 */
export const userSettingsAPI = {
  /**
   * Get cooldown range settings
   * @returns {Promise<Object>} Cooldown range data
   */
  getCooldownRange: async () => {
    const queryParams = new URLSearchParams();
    appendUserIdQueryParam(queryParams);
    const queryString = queryParams.toString();
    const endpoint = `/user-settings/cooldown-range${
      queryString ? `?${queryString}` : ""
    }`;
    const response = await apiRequest(endpoint, {
      method: "GET",
    });
    return response;
  },

  /**
   * Set cooldown range for the user (PUT — create or replace per API contract).
   * @param {Object} cooldownRange - Cooldown range data { min, max }
   * @returns {Promise<Object>} Cooldown range configuration
   */
  updateCooldownRange: async (cooldownRange) => {
    const queryParams = new URLSearchParams();
    appendUserIdQueryParam(queryParams);
    const queryString = queryParams.toString();
    const endpoint = `/user-settings/cooldown-range${
      queryString ? `?${queryString}` : ""
    }`;
    const response = await apiRequest(endpoint, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(cooldownRange),
    });
    return response;
  },
};
